/**
 * Product imagery for comparison views.
 * Exact product photos should always win. If we do not have an exact match,
 * the UI should show an honest "photo unavailable" state instead of a wrong lookalike.
 */
import type { Product } from "@/data/productDatabase";
import subSundown from "@/assets/products/sub-sundown.png";
import subJL from "@/assets/products/sub-jl.png";
import subSkar from "@/assets/products/sub-skar.png";
import subRockford from "@/assets/products/sub-rockford.png";
import subKicker from "@/assets/products/sub-kicker.png";
import subAlpine from "@/assets/products/sub-alpine.png";
import subFi from "@/assets/products/sub-fi.png";
import subDD from "@/assets/products/sub-dd.png";
import subDS18 from "@/assets/products/sub-ds18.png";
import subDeafBonce from "@/assets/products/sub-deafbonce.png";
import subGeneric from "@/assets/products/sub-generic.png";

import ampSundown from "@/assets/products/amp-sundown.png";
import ampJL from "@/assets/products/amp-jl.png";
import ampSkar from "@/assets/products/amp-skar.png";
import ampRockford from "@/assets/products/amp-rockford.png";
import ampTaramps from "@/assets/products/amp-taramps.png";
import ampGeneric from "@/assets/products/amp-generic.png";

import speakerGeneric from "@/assets/products/speaker-generic.png";
import tweeterGeneric from "@/assets/products/tweeter-generic.png";

type ProductCategory = "subwoofer" | "amplifier" | "speaker" | "tweeter";

/** Exact product → verified real photo URL (all tested 200 OK) */
const PRODUCT_IMAGES: Record<string, string> = {
  // ── Sundown Audio (from sundownaudio.com Shopify CDN) ──
  "sub-sundown-u12": "https://sundownaudio.com/cdn/shop/files/U-12-000.png?v=1695062061&width=80",
  "sub-sundown-u15": "https://sundownaudio.com/cdn/shop/files/U-15-000.png?v=1695062186&width=80",

  // ── JL Audio (from creativeaudio.net BigCommerce CDN) ──
  "sub-jl-12w7": "https://cdn11.bigcommerce.com/s-oqfwjl/products/165/images/267/12w7ae-3__1__81585.1758043699.500.500.jpg?c=2",
  "sub-jl-10w7": "https://cdn11.bigcommerce.com/s-oqfwjl/products/209/images/271/10w7ae-3__1__88419.1758043700.500.500.jpg?c=2",
  "sub-jl-13w7": "https://cdn11.bigcommerce.com/s-oqfwjl/products/155/images/444/13w7ae-d1.5__1__09498.1758043700.500.500.jpg?c=2",
  "sub-jl-12w6": "https://cdn11.bigcommerce.com/s-oqfwjl/products/71409/images/484791/12w6v3-d4__1__68693.1758043700.500.500.jpg?c=2",
  "sub-jl-10tw3": "https://cdn11.bigcommerce.com/s-oqfwjl/products/489/images/337/10tw3-d4__1__47603.1758043700.500.500.jpg?c=2",
  "sub-jl-12tw3": "https://cdn11.bigcommerce.com/s-oqfwjl/products/489/images/337/10tw3-d4__1__47603.1758043700.500.500.jpg?c=2",
  "sub-jl-10w3": "https://cdn11.bigcommerce.com/s-oqfwjl/products/209/images/271/10w7ae-3__1__88419.1758043700.500.500.jpg?c=2",

  // ── Skar Audio (from skar-audio-product-images S3 CDN) ──
  "sub-skar-evl12": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/EVL-12%20D4/webp/EVL-12%20D4-1.webp",
  "sub-skar-evl15": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/EVL-15%20D2/webp/EVL-15%20D2-1.webp",
  "sub-skar-zvx15": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/ZVX-15v2%20D1/webp/ZVX-15v2%20D1-1.webp",
  "sub-skar-zvx12": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/ZVX-12v2%20D1/webp/ZVX-12v2%20D1-1.webp",
  "sub-skar-ddx12": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/DDX-12%20D4/webp/DDX-12%20D4-1.webp",
  "sub-skar-sdr12": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/SDR-12%20D4/webp/SDR-12%20D4-1.webp",
  "sub-skar-vxf12": "https://skar-audio-product-images.s3.amazonaws.com/products/subwoofers/VXF-12%20D2/webp/VXF-12%20D2-1.webp",

  // ── Rockford Fosgate (from rockfordfosgate.com) ──
  "sub-rf-p3d2": "https://rockfordfosgate.com/media/filer_public/07/12/0712b0f1-7075-487f-b3b9-72108810913b/p3d2-12_1.png",
  "sub-rf-p2d2-12": "https://rockfordfosgate.com/media/filer_public/07/12/0712b0f1-7075-487f-b3b9-72108810913b/p3d2-12_1.png",

  // ── Kicker (from kicker.com) ──
  "sub-kicker-l7r": "https://www.kicker.com/app/products/caraudio/subs/l7r/p45_l7_r122/images/480x360-front.jpg",
  "sub-kicker-l7r15": "https://www.kicker.com/app/products/caraudio/subs/l7r/p45_l7_r152/images/480x360-front.jpg",

  // ── Skar Audio amps (from S3 CDN) ──
  "amp-skar-rp2000": "https://skar-audio-product-images.s3.amazonaws.com/products/amplifiers/RP-2000.1D/webp/RP-2000.1D-1.webp",
  "amp-skar-rp1500": "https://skar-audio-product-images.s3.amazonaws.com/products/amplifiers/RP-1500.1D/webp/RP-1500.1D-1.webp",
  "amp-skar-rp800": "https://skar-audio-product-images.s3.amazonaws.com/products/amplifiers/RP-800.1D/webp/RP-800.1D-1.webp",
  "amp-skar-rp75-4ab": "https://skar-audio-product-images.s3.amazonaws.com/products/amplifiers/RP-75.4AB/webp/RP-75.4AB-1.webp",

  // ── JL Audio amps ──
  "amp-jl-rd1000": "https://cdn11.bigcommerce.com/s-oqfwjl/products/5314/images/12883/rd1000-1-frt__64466.1667483760.500.500.jpg",

  // ── Rockford Fosgate amps ──
  "amp-rf-t1500": "https://rockfordfosgate.com/media/filer_public/eb/6a/eb6abfb4-ad7e-4606-b3c1-e3a0ca800986/t1500-1bdcp_1.png",
  "amp-rf-r1200": "https://rockfordfosgate.com/media/filer_public/bd/0f/bd0ffd57-7cce-4ec1-9611-e040e8197d8b/r2-1200x1_1.png",

  // ── DS18 amps ──
  "amp-ds18-exl3500": "https://cdn.shopify.com/s/files/1/0018/0142/0861/files/IMG_5290.jpg",
};

/** Brand → category → local image */
const BRAND_IMAGES: Record<string, Partial<Record<ProductCategory, string>>> = {
  "Sundown Audio": { subwoofer: subSundown, amplifier: ampSundown },
  "JL Audio": { subwoofer: subJL, amplifier: ampJL },
  "Skar Audio": { subwoofer: subSkar, amplifier: ampSkar },
  "Rockford Fosgate": { subwoofer: subRockford, amplifier: ampRockford },
  "Kicker": { subwoofer: subKicker },
  "Alpine": { subwoofer: subAlpine },
  "Fi Audio": { subwoofer: subFi },
  "DD Audio": { subwoofer: subDD },
  "DS18": { subwoofer: subDS18 },
  "Deaf Bonce": { subwoofer: subDeafBonce },
  "Taramps": { amplifier: ampTaramps },
};

/** Category-level fallback images */
const CATEGORY_FALLBACK: Record<ProductCategory, string> = {
  subwoofer: subGeneric,
  amplifier: ampGeneric,
  speaker: speakerGeneric,
  tweeter: tweeterGeneric,
};

/**
 * Returns the exact product image when available.
 * Falls back to product.image for future per-item data, then optional brand/category art.
 */
export function getProductImage(product: Product, options?: { allowFallback?: boolean }): string | null {
  if (PRODUCT_IMAGES[product.id]) return PRODUCT_IMAGES[product.id];
  if (product.image) return product.image;
  // Fall back to brand/category image — still better than nothing
  return BRAND_IMAGES[product.brand]?.[product.category] ?? CATEGORY_FALLBACK[product.category] ?? null;
}
